import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  AiConversationFeatureEnum,
  AiConversationFragmentFragment,
  AiMessageFeedbackEnum,
  AiMessageFeedbackReasonEnum,
  AiMessageRoleEnum,
  AiMessageStatusEnum,
  AiToolStatusEnum,
  User,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import toast from '@lib/components/Toast/Toast';
import { logger } from '@lib/services/logger';
import useAiConversation, {
  getAiMessagePlainText,
  hasTurnUserMessage,
} from './hooks/useAiConversation';
import useAiBudgetStatus from './hooks/useAiBudgetStatus';
import useAiMessageFeedback from './hooks/useAiMessageFeedback';
import useStickToBottom from './hooks/useStickToBottom';
import useFillViewportWithOlderPages from './hooks/useFillViewportWithOlderPages';
import MessageBubble from './MessageBubble';
import AiDislikeFeedbackModal from './AiDislikeFeedbackModal';
import ToolActivityRow from './ToolActivityRow';
import { AiToolStep } from './ToolStepsStrip';
import ConversationEmptyState from './ConversationEmptyState';
import ChatInput, { ChatInputHandle } from './ChatInput';
import AiChatInsertContext, { AiChatDocRef } from './AiChatInsertContext';
import AiErrorBanner, { AiErrorMessage } from './AiErrorBanner';
import { AiAssistantTexts } from './getAiAssistantTexts';
import {
  getAiBudgetBlockedAction,
  getAiBudgetBlockedText,
} from './utils/getAiBudgetBlock';
import { getAiMessageErrorText } from './utils/getAiErrorText';
import getAiErrorAction, {
  AiErrorAction,
  isAiConversationClosedOutcome,
  isAiUnavailableCode,
} from './utils/getAiErrorAction';
import isAiToolActivityMessage from './utils/isAiToolActivityMessage';
import isRenderableAiMessage from './utils/isRenderableAiMessage';
import styles from './AiAssistant.module.scss';

type Props = {
  conversationId: string | null;
  busy?: boolean;
  // The conversation exhausted its context window and the BE flipped it to
  // CLOSED: it rejects every further message (guide §3), so the composer is
  // removed entirely rather than disabled — nothing typed here could ever send.
  // Also settles the turn that closed it (see useAiConversation), which is what
  // puts the "conversation too long" banner on screen.
  closed?: boolean;
  user: User;
  feature: AiConversationFeatureEnum;
  texts: AiAssistantTexts;
  onConversationCreated: (conversation: AiConversationFragmentFragment) => void;
  onConversationsChanged: () => void;
  onTurnSettled?: (status: AiMessageStatusEnum | null) => void;
  // Switch to a fresh chat — the only way forward out of a closed conversation.
  onStartNewChat: () => void;
  // Open the plan modal, offered when the company is out of AI credits.
  onUpgradePlan: () => void;
  // Documents pre-attached to the composer when the pane mounts.
  initialDocs?: AiChatDocRef[];
};

// One mounted instance per pane session — the parent remounts (via paneKey)
// on an explicit conversation switch so all turn state resets, but keeps the
// instance alive when the first sent message auto-creates a conversation so the
// optimistic user message and in-flight stream survive the id transition.
function ConversationPane(props: Props) {
  const {
    conversationId,
    busy,
    closed,
    user,
    feature,
    texts,
    onConversationCreated,
    onConversationsChanged,
    onTurnSettled,
    onStartNewChat,
    onUpgradePlan,
    initialDocs,
  } = props;
  const { t } = useTranslation();
  // Set right before an older-page load so the layout effect can restore the
  // scroll position once the prepended messages render (instead of jumping to
  // the bottom). Holds the scroll metrics captured before the prepend.
  const prependRef = useRef<{ prevHeight: number; prevTop: number } | null>(
    null,
  );
  const prevFirstIdRef = useRef<string | undefined>(undefined);
  // The viewport follows new content to the bottom while the user is parked
  // there, and lets go the moment they scroll up to read history — otherwise a
  // streaming turn would yank them back down on every delta. Stands down while
  // an older page is being prepended, which restores its own position below.
  const isPrepending = useCallback(() => prependRef.current !== null, []);
  const {
    registerScrollNode,
    setContentNode,
    scrollRef: listRef,
    scrollNode,
    contentNode,
    pinned,
    pinToBottom,
    handleScroll,
  } = useStickToBottom({ isPaused: isPrepending });
  // Mirrors `pinned` for useAiConversation's settle path, which needs the
  // CURRENT value at an arbitrary later point (a turn settling), not a value
  // frozen at whichever render its closure captured.
  const pinnedRef = useRef(true);
  pinnedRef.current = pinned;
  const isPinned = useCallback(() => pinnedRef.current, []);
  // Lets an inline doc chip pin a document reference to the composer via
  // AiChatInsertContext (imperative attach on ChatInput).
  const chatInputRef = useRef<ChatInputHandle>(null);
  const insertIntoChat = useCallback((doc: AiChatDocRef) => {
    chatInputRef.current?.attachDocument(doc);
  }, []);

  // The pane is keyed by conversationId — one mount per conversation.
  useEffect(() => {
    logger.debug('AiAssistant: conversation pane mounted', {
      conversationId,
      busy,
      feature,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    messages,
    transcriptSettled,
    loadingMessages,
    turnStartNewestId,
    hasMoreOlder,
    loadingOlder,
    olderPagesFailed,
    loadOlderMessages,
    flushStaleOlderPages,
    pendingUserMessage,
    turnText,
    turnThinkingText,
    turnMessageId,
    turnStatus,
    turnErrorCode,
    turnSuggestions,
    isThinking,
    runningToolName,
    turnToolSteps,
    isGenerating,
    finishedTurnError,
    sendMessage,
    stopGeneration,
  } = useAiConversation({
    conversationId,
    busy,
    closed,
    feature,
    onConversationCreated,
    onConversationsChanged,
    onTurnSettled,
    isPinned,
  });

  // Remaining AI credits for the composer footer (Figma 31921:279528), plus the
  // live "may the user spend at all" verdict. The pane only mounts inside the
  // plan-gated AI modal, so the budget query is enabled. Refetch when a turn
  // settles (isGenerating true → false) since a reply consumes credits.
  const {
    credits: aiCredits,
    blockedReason: aiBlockedReason,
    refetch: refetchAiBudget,
  } = useAiBudgetStatus(true);
  const prevGeneratingRef = useRef(false);
  useEffect(() => {
    if (prevGeneratingRef.current && !isGenerating) {
      refetchAiBudget();
    }
    prevGeneratingRef.current = isGenerating;
  }, [isGenerating, refetchAiBudget]);

  const { submitFeedback, removeFeedback, feedbackLoading } =
    useAiMessageFeedback();
  // The assistant message whose dislike modal is open (null = closed).
  const [dislikeTargetId, setDislikeTargetId] = useState<string | null>(null);

  const handleLike = useCallback(
    (id: string) => {
      submitFeedback(id, AiMessageFeedbackEnum.Positive).catch(() => {
        toast({ content: t('ai-feedback-failed') });
      });
    },
    [submitFeedback, t],
  );
  const handleDislike = useCallback((id: string) => {
    setDislikeTargetId(id);
  }, []);
  const handleRemoveFeedback = useCallback(
    (id: string) => {
      removeFeedback(id).catch(() => {
        toast({ content: t('ai-feedback-failed') });
      });
    },
    [removeFeedback, t],
  );
  const handleDislikeSubmit = useCallback(
    (reason: AiMessageFeedbackReasonEnum, comment: string) => {
      if (!dislikeTargetId) return;
      submitFeedback(
        dislikeTargetId,
        AiMessageFeedbackEnum.Negative,
        reason,
        comment || undefined,
      )
        .then(() => setDislikeTargetId(null))
        .catch(() => {
          // Keep the modal open on failure so the user can retry the submit.
          toast({ content: t('ai-feedback-failed') });
        });
    },
    [dislikeTargetId, submitFeedback, t],
  );

  // Render settled server rows only; the in-flight assistant message is drawn
  // from the local stream state to avoid duplicates. Tool-activity rows
  // (empty-content assistant messages with toolCalls) are kept and rendered as
  // an activity row rather than a bubble, and blank terminal-failure markers are
  // kept for their error banner — but only when there is one to show.
  //
  // Memoized because ConversationPane also re-renders for reasons that have
  // nothing to do with the transcript — most notably `pinned` flipping on a
  // scroll — and this filter, along with the renderGroups build below, was
  // re-running on every one of those, over the FULL loaded message list, on
  // every scroll tick. That's what made the transcript's own scrollbar feel
  // laggier than the sidebar's, which has no such per-scroll recomputation at
  // all.
  const visibleMessages = useMemo(
    () =>
      messages.filter((message) =>
        isRenderableAiMessage(
          message,
          getAiMessageErrorText(t, message.status, message.errorCode),
        ),
      ),
    [messages, t],
  );

  // Collapse each assistant turn into a single avatar group: the consecutive
  // tool-activity rows that run before an answer are buffered and folded into
  // that answer bubble as a steps header (see ToolStepsStrip), instead of each
  // rendering its own duplicated avatar row.
  type RenderGroup =
    | {
        kind: 'bubble';
        key: string;
        message: (typeof visibleMessages)[number];
        toolSteps: AiToolStep[];
        thinking: string | null;
      }
    | { kind: 'tools'; key: string; toolSteps: AiToolStep[] }
    | {
        kind: 'error';
        key: string;
        message: (typeof visibleMessages)[number];
        errorText: string;
        toolSteps: AiToolStep[];
      };
  // See the `visibleMessages` memo above for why: same O(messages) cost, same
  // scroll-driven re-render exposure. `renderGroups` is never mutated after
  // this point — the conditional trailing "orphan tools" row (a turn that
  // ended on tools with no following answer) is appended when `rows` is built
  // below instead of pushed here, so a cache hit can safely return the same
  // array on a later render without a stale row accumulating onto it.
  const { lastAssistantId, renderGroups, orphanToolSteps } = useMemo(() => {
    const groups: RenderGroup[] = [];
    let pendingToolSteps: AiToolStep[] = [];
    // Reasoning persisted on the tool-activity rows that lead an answer. An
    // EXTENDED turn that calls a tool persists its reasoning on the
    // intermediate tool row and answers on a SEPARATE row (which may carry no
    // reasoning of its own), so folding only the tool steps drops the
    // thoughts on settle — the panel that streamed the whole turn's reasoning
    // vanishes the moment the live bubble hands off to the persisted rows.
    // Carry the reasoning onto the answer bubble too so it survives, matching
    // what the live stream (whose buffer spans the whole turn) already
    // showed.
    let pendingThinking: string[] = [];
    visibleMessages.forEach((message) => {
      if (isAiToolActivityMessage(message)) {
        pendingToolSteps = pendingToolSteps.concat(
          // Keyed by toolCallId, not the row id: it is the same identifier the
          // stream's TOOL frames carry, so a step already on screen from the live
          // turn reconciles with its persisted row instead of rendering twice.
          message.toolCalls.map((toolCall) => ({
            id: toolCall.toolCallId,
            toolName: toolCall.toolName,
          })),
        );
        if (message.thinking) pendingThinking.push(message.thinking);
        return;
      }
      const isUser = message.role === AiMessageRoleEnum.User;
      const errorText = isUser
        ? null
        : getAiMessageErrorText(t, message.status, message.errorCode);
      // A failed turn splits into up to two groups: whatever text it managed to
      // stream stays an ordinary bubble (it is what the assistant said), and the
      // failure renders as a banner beneath it (it is what happened to the turn).
      // A turn that failed before any text has only the banner.
      if (isUser || message.content) {
        groups.push({
          kind: 'bubble',
          key: message.id,
          message,
          toolSteps: pendingToolSteps,
          // The answer's own reasoning last, after any folded from lead-in tool
          // rows, so the panel reads in turn order.
          thinking: isUser
            ? null
            : [...pendingThinking, message.thinking]
                .filter(Boolean)
                .join('\n\n') || null,
        });
        pendingToolSteps = [];
        pendingThinking = [];
      }
      if (errorText) {
        groups.push({
          kind: 'error',
          key: `${message.id}-error`,
          message,
          errorText,
          toolSteps: pendingToolSteps,
        });
        pendingToolSteps = [];
        // A turn that ended in a banner also closes the reasoning window; drop any
        // folded thoughts so they don't bleed onto the next turn's answer.
        pendingThinking = [];
      }
    });
    return {
      lastAssistantId: [...visibleMessages]
        .reverse()
        .find(
          (message) =>
            message.role === AiMessageRoleEnum.Assistant &&
            !isAiToolActivityMessage(message),
        )?.id,
      renderGroups: groups,
      // Tool rows with no following answer in the transcript (a turn that
      // ended on tools — e.g. cancelled/failed before any answer token
      // persisted). These fold into the in-flight turn bubble below so the
      // whole turn stays one avatar group, exactly like a normal answer; when
      // no such bubble renders they flush as a standalone steps-only row
      // (orphanTools group, appended after showTurnBubble when `rows` is
      // built below).
      orphanToolSteps: pendingToolSteps,
    };
  }, [visibleMessages, t]);

  // Steps for whichever group trails the turn — the streamed bubble, or the
  // outcome banner when the turn failed without saying anything: the live
  // stream's tools (running ones shown as an in-progress step, completed ones as
  // a check) plus any already-persisted orphan rows (a conversation reopened
  // mid-turn has both).
  // Live steps render the moment their RUNNING frame lands rather than waiting
  // ~3s for the post-turn refetch, and dedupe by toolCallId keeps an invocation
  // present in both sources from rendering twice — the persisted (completed)
  // orphan wins over a live duplicate since it is listed first.
  const liveTurnToolSteps: AiToolStep[] = turnToolSteps.map((step) => ({
    id: step.id,
    toolName: step.toolName,
    running: step.status !== AiToolStatusEnum.Completed,
  }));
  const turnGroupToolSteps = [...orphanToolSteps, ...liveTurnToolSteps].filter(
    (step, index, steps) =>
      steps.findIndex((other) => other.id === step.id) === index,
  );

  // The BE persists the user message immediately, so once the transcript
  // contains it the optimistic copy would render as a duplicate bubble — drop it
  // as soon as THIS turn's user message appears in the transcript. Scope the
  // match to rows persisted since the turn started (hasTurnUserMessage): a plain
  // content match also hits a PREVIOUS identical prompt, which would suppress the
  // fresh optimistic bubble for the whole thinking window when the same text is
  // re-sent, so the just-typed message stays invisible until the post-turn
  // refetch. Match on the full ascending `messages` (user rows are always
  // renderable, so filtering to `visibleMessages` is unnecessary and would only
  // drop the older pages the turn-start boundary may live in).
  const showPendingUserMessage =
    !!pendingUserMessage &&
    !hasTurnUserMessage(
      messages,
      turnStartNewestId,
      pendingUserMessage.content,
    );

  // Only take over the whole pane with a spinner on a cold open; never while a
  // turn is in flight (creating a new conversation flips the messages query to
  // loading, which must not blank out the optimistic message + thinking).
  const showFullLoader = loadingMessages && !pendingUserMessage;

  // Keep the streamed bubble until the persisted assistant message (same id the
  // stream used) lands in the transcript — hiding it on terminal status alone
  // leaves a ~0.5s gap where neither the streamed nor the persisted answer
  // shows while the post-turn refetch is in flight.
  const streamedAnswerPersisted =
    !!turnMessageId &&
    visibleMessages.some((message) => message.id === turnMessageId);
  const showTurnBubble =
    isGenerating || (!!turnText && !streamedAnswerPersisted);

  // This turn's outcome, taken from the earliest source that knows it. The live
  // stream's terminal frame is first by ~1.2s — it is what TRIGGERS the refetch
  // that brings the persisted row in — and reading it is what keeps the error and
  // the composer on the same clock: they are two halves of one answer to "what
  // happened to this turn", and either one arriving alone reads as a bug (an open
  // composer under a dead chat's banner, or an input that vanishes for a second
  // with nothing yet saying why). `finishedTurnError` covers a turn the BE failed
  // without ever persisting a row; the newest transcript row covers a reload,
  // when the live turn state is gone.
  //
  // The live frame hands over the moment the transcript carries the outcome —
  // `transcriptSettled`, the very signal the hook settles the turn on — so the
  // two never both describe the turn, and never both leave it undescribed.
  const lastVisibleMessage = visibleMessages[visibleMessages.length - 1];
  const lastOutcomeMessage =
    lastVisibleMessage?.role === AiMessageRoleEnum.Assistant
      ? lastVisibleMessage
      : undefined;
  const liveTurnOutcome =
    turnStatus && !transcriptSettled
      ? { status: turnStatus, errorCode: turnErrorCode }
      : null;
  // Mutually exclusive: settleTurnWithError resets the stream state as it sets
  // the inline error, so at most one of these is ever non-null.
  const turnOutcome = finishedTurnError ?? liveTurnOutcome;
  const latestErrorCode = turnOutcome
    ? turnOutcome.errorCode
    : lastOutcomeMessage?.errorCode || null;
  const latestStatus = turnOutcome
    ? turnOutcome.status
    : (lastOutcomeMessage?.status ?? null);

  // Copy for an outcome the transcript does not carry — yet, or ever. Null for a
  // turn with nothing to report: a success, or one whose outcome is deliberately
  // silent (the user's own Stop). That is why every use is gated on the TEXT
  // rather than on turnOutcome itself — which holds a value for every turn,
  // including a healthy streaming one — since an ungated banner would draw an
  // empty one, the same way a blank CANCELLED row once drew a bubble holding
  // nothing but a timestamp (see isRenderableAiMessage).
  const turnOutcomeErrorText = turnOutcome
    ? getAiMessageErrorText(t, turnOutcome.status, turnOutcome.errorCode)
    : null;

  // A trailing turn bubble (the in-flight streamed answer) or the turn-outcome
  // banner absorbs the orphan tool steps into its own avatar group; flush them as
  // a standalone row only when neither renders. When both render — a turn that
  // streamed text and then failed — the bubble takes them and the banner passes
  // none, so they never draw twice.
  const trailingGroupAbsorbsTools = showTurnBubble || !!turnOutcomeErrorText;
  // Appended to `rows` below rather than pushed onto the memoized
  // `renderGroups` — see that memo's comment for why mutating it would leak a
  // stale row across renders.
  const orphanToolsRow: RenderGroup | null =
    orphanToolSteps.length && !trailingGroupAbsorbsTools
      ? { kind: 'tools', key: 'orphan-tools', toolSteps: orphanToolSteps }
      : null;
  const isEmpty =
    !visibleMessages.length &&
    !showPendingUserMessage &&
    !showTurnBubble &&
    !turnOutcomeErrorText;

  // The three trailing rows the transcript can carry after the settled
  // groups above — the optimistic user bubble, the in-flight streamed answer,
  // and this turn's outcome banner — folded into the SAME list the groups
  // live in, so the transcript renders from one uniform row list.
  type TrailingRow =
    | { kind: 'pendingUser'; key: 'pending-user' }
    | { kind: 'turnBubble'; key: 'turn-bubble' }
    | { kind: 'turnOutcome'; key: 'turn-outcome' };
  const trailingRows: TrailingRow[] = [];
  if (showPendingUserMessage) {
    trailingRows.push({ kind: 'pendingUser', key: 'pending-user' });
  }
  if (showTurnBubble) {
    trailingRows.push({ kind: 'turnBubble', key: 'turn-bubble' });
  }
  if (turnOutcome && turnOutcomeErrorText) {
    trailingRows.push({ kind: 'turnOutcome', key: 'turn-outcome' });
  }
  type TranscriptRow = RenderGroup | TrailingRow;
  const rows: TranscriptRow[] = [
    ...renderGroups,
    ...(orphanToolsRow ? [orphanToolsRow] : []),
    ...trailingRows,
  ];

  // The deferred older-pages realign (useAiConversation.settleOlderPages)
  // waits for the user to come back to the bottom before dropping content out
  // from under them; this is the "they're back" signal that flushes it. A
  // no-op when nothing was deferred.
  useEffect(() => {
    flushStaleOlderPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned]);

  // Every send path (composer, empty-state suggestion, follow-up chip) routes
  // through here so the viewport re-arms bottom-follow — the user expects to
  // jump to the message they just sent even if they had scrolled up.
  //
  // No thinking level is passed: the assistant is single-level EXTENDED-only
  // for this feature and the picker is hidden, so `attributes.thinkingLevel` is
  // omitted on every send (BE guide §3) — see AiAssistant/README.md for the
  // thinking-picker/mode-switcher history and how to bring it back.
  const handleSend = useCallback(
    (content: string) => {
      pinToBottom();
      sendMessage(content);
    },
    [pinToBottom, sendMessage],
  );

  // AI is off for the whole company, not just this turn. Two independent
  // signals, because neither alone is complete: `blockedReason` is the live
  // truth and covers every chat including a brand-new one, but it only reports a
  // REAL block — a mocked budget_exhausted (QA) leaves the budget untouched, and
  // the error code is the only trace of it.
  //
  // Never derived from the credits: they round up, so `remaining` can read 0
  // while spend is still admitted, and an unconfigured budget refuses every call
  // while reporting no cap at all (BE guide §10).
  const aiUnavailable =
    !!aiBlockedReason || isAiUnavailableCode(latestErrorCode);
  // This conversation is closed server-side. The `closed` prop alone is the right
  // answer far too late: it comes from the conversations-list refetch that trails
  // the turn, a whole round-trip (~2s) behind the outcome above — which would
  // leave the composer open, and enabled, underneath a banner saying the chat is
  // over. The turn's own outcome says the same thing at the same moment the
  // banner does, and `closed` stays as the backstop for everything else (a
  // conversation opened when it was already closed, a lost terminal frame).
  const conversationClosed =
    closed || isAiConversationClosedOutcome(latestStatus, latestErrorCode);
  // No composer at all when nothing typed here could ever send: a closed
  // conversation rejects every message (guide §3), and an account-wide block
  // fails the send before it starts. A field that cannot be used is worse than
  // no field — it invites typing that goes nowhere.
  const composerHidden = conversationClosed || aiUnavailable;

  // Hiding the composer also removes the credits line, so an account-wide block
  // has to state itself where the composer was. Skipped only when the last turn
  // failed with an unavailable code, whose banner already says the same thing;
  // exhausted credits otherwise reach here from any state — a fresh chat that
  // never sent anything, or one whose last turn failed for an unrelated reason
  // — and without this each shows an empty pane, no input, and no reason.
  const showUnavailableNotice =
    aiUnavailable &&
    !conversationClosed &&
    !isAiUnavailableCode(latestErrorCode);

  // Re-send the prompt the failed turn was answering. Prefer the optimistic copy
  // (a turn that failed before the BE persisted anything has only that) and fall
  // back to the newest user row.
  const lastUserPrompt =
    pendingUserMessage?.content ??
    [...visibleMessages]
      .reverse()
      .filter((message) => message.role === AiMessageRoleEnum.User)
      .map(getAiMessagePlainText)
      .find(Boolean);
  const handleRetry = useCallback(() => {
    if (lastUserPrompt) handleSend(lastUserPrompt);
  }, [lastUserPrompt, handleSend]);
  // Needs a prompt to re-send, and somewhere to send it: a retry offered while
  // the composer is gone would fail exactly as the original did.
  const canRetry = !!lastUserPrompt && !composerHidden;

  // Single summary of what the user is actually seeing, logged whenever it
  // changes — makes the rendered state observable from the debug log.
  let displayState = 'messages';
  if (showFullLoader) displayState = 'loading';
  else if (isEmpty) displayState = 'empty-state';
  else if (turnOutcomeErrorText)
    displayState = `error:${turnOutcome?.errorCode ?? 'generic'}`;
  else if (showTurnBubble && isThinking)
    displayState = runningToolName ? 'tool-activity' : 'thinking';
  else if (showTurnBubble) displayState = 'streaming-text';
  // Whether the composer is on screen — the other half of what the user sees,
  // and the half the error banner has to agree with.
  const composerState = composerHidden ? 'hidden' : 'open';
  useEffect(() => {
    logger.debug('AiAssistant: render state', {
      conversationId,
      displayState,
      composerState,
      latestErrorCode,
      aiBlockedReason,
      visibleMessages: visibleMessages.length,
      hasPendingUserMessage: !!pendingUserMessage,
      isThinking,
      isGenerating,
      runningToolName,
      persistedToolRows: visibleMessages.filter(isAiToolActivityMessage).length,
      orphanToolSteps: orphanToolSteps.length,
      turnToolSteps: turnToolSteps.length,
      turnGroupToolSteps: turnGroupToolSteps.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayState,
    composerState,
    latestErrorCode,
    visibleMessages.length,
    runningToolName,
    orphanToolSteps.length,
    turnToolSteps.length,
  ]);

  // Bottom-follow itself is not driven from here: useStickToBottom observes the
  // transcript's height directly, so it catches the growth no render announces —
  // a markdown image decoding, a code block laying out, the composer growing —
  // which a key built from rendered state can only guess at. This effect is left
  // with the one job that genuinely needs the pre-paint moment: undoing the jump
  // a prepended page would otherwise cause.
  const firstVisibleId = visibleMessages[0]?.id;
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // No prepend in flight: keep the baseline current, so a restore compares
    // against the id that was actually on screen when its load started.
    if (!prependRef.current) {
      prevFirstIdRef.current = firstVisibleId;
      return;
    }
    // An older-page load is pending: do nothing until the prepended messages
    // actually land (detected by the first rendered id changing), then restore
    // the prior scroll position so the viewport stays put instead of jumping.
    if (firstVisibleId !== prevFirstIdRef.current) {
      const { prevHeight, prevTop } = prependRef.current;
      list.scrollTop = list.scrollHeight - prevHeight + prevTop;
      prependRef.current = null;
      prevFirstIdRef.current = firstVisibleId;
    } else if (!loadingOlder) {
      // The older-page load finished without prepending anything (it failed, or
      // returned an empty page). Release the latch — otherwise it stays set
      // forever, permanently wedging both bottom-follow (which stands down while
      // it is held) and pagination (handleListScroll early-returns on it), and a
      // later unrelated id change would restore a scroll position computed from
      // stale metrics.
      prependRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstVisibleId, loadingOlder]);

  // Request the next older page, capturing the scroller's metrics first so the
  // layout effect above can keep the viewport anchored once the prepended
  // messages land. Shared by both triggers — the near-top scroll below and the
  // no-overflow fill — so they anchor identically.
  const loadOlderPage = useCallback(
    (list: HTMLElement) => {
      prependRef.current = {
        prevHeight: list.scrollHeight,
        prevTop: list.scrollTop,
      };
      loadOlderMessages();
    },
    // loadOlderMessages is re-created every render by its hook, so this
    // callback is too — which is fine: nothing subscribes to its identity, and
    // what matters is that the closure is current.
    [loadOlderMessages],
  );

  const handleListScroll = () => {
    handleScroll();
    // Top-of-list near-edge: pull the next older page.
    const list = listRef.current;
    if (!list || !hasMoreOlder || loadingOlder || prependRef.current) return;
    if (list.scrollTop <= 60) loadOlderPage(list);
  };

  // The near-top trigger above only runs on scroll events, which a transcript
  // that fits its viewport never produces — see the hook for the deadlock that
  // leaves and why it watches geometry rather than renders.
  useFillViewportWithOlderPages({
    scrollNode,
    contentNode,
    enabled: hasMoreOlder && !loadingOlder && !olderPagesFailed,
    isPaused: isPrepending,
    onFill: loadOlderPage,
  });

  // One row of the transcript (see `rows` above).
  function renderRow(row: TranscriptRow) {
    if (row.kind === 'tools') {
      return <ToolActivityRow toolCalls={row.toolSteps} />;
    }
    if (row.kind === 'error') {
      // Only the turn that just failed gets an action — offering "try again"
      // on a failure the conversation has already moved past would re-send a
      // stale prompt.
      const isLatest = row.message.id === lastVisibleMessage?.id;
      return (
        <AiErrorBanner
          text={row.errorText}
          toolSteps={row.toolSteps}
          action={
            isLatest
              ? getAiErrorAction(row.message.status, row.message.errorCode)
              : AiErrorAction.None
          }
          onStartNewChat={onStartNewChat}
          onUpgradePlan={onUpgradePlan}
          onRetry={canRetry ? handleRetry : undefined}
        />
      );
    }
    if (row.kind === 'bubble') {
      const { message, toolSteps } = row;
      const isUser = message.role === AiMessageRoleEnum.User;
      const isLastAssistant = message.id === lastAssistantId;
      const messageSuggestions = message.suggestedFollowups.length
        ? message.suggestedFollowups
        : turnSuggestions;
      return (
        <MessageBubble
          isUser={isUser}
          thinking={isUser ? undefined : row.thinking}
          toolSteps={isUser ? undefined : toolSteps}
          messageId={message.id}
          content={message.content}
          contentBlocks={message.contentBlocks}
          citations={message.citations}
          createdAt={message.createdAt as string}
          suggestions={isLastAssistant ? messageSuggestions : []}
          suggestionsDisabled={isGenerating}
          onSuggestionSelect={
            isLastAssistant && !isUser ? handleSend : undefined
          }
          showFeedback={
            !isUser && message.status === AiMessageStatusEnum.Complete
          }
          feedback={message.feedback}
          feedbackDisabled={feedbackLoading}
          onLike={handleLike}
          onDislike={handleDislike}
          onRemoveFeedback={handleRemoveFeedback}
          user={isUser ? user : undefined}
        />
      );
    }
    if (row.kind === 'pendingUser') {
      return (
        <MessageBubble
          isUser
          content={pendingUserMessage?.content}
          createdAt={pendingUserMessage?.createdAt}
          user={user}
        />
      );
    }
    if (row.kind === 'turnBubble') {
      return (
        <MessageBubble
          isThinking={isThinking}
          thinking={turnThinkingText}
          isStreaming
          toolSteps={turnGroupToolSteps}
          content={turnText}
        />
      );
    }
    // row.kind === 'turnOutcome': this turn's failure, before (or instead of)
    // its persisted row — the live terminal frame, or a turn the BE failed
    // without persisting anything at all (see useAiConversation's
    // failedUnpersisted). Same banner as a persisted failure, and always the
    // latest: the live turn state only ever holds the current turn. The steps
    // go to the bubble when there is one, so a turn that streamed text before
    // failing shows them once.
    return (
      <AiErrorBanner
        text={turnOutcomeErrorText as string}
        toolSteps={showTurnBubble ? undefined : turnGroupToolSteps}
        action={getAiErrorAction(
          (turnOutcome as NonNullable<typeof turnOutcome>).status,
          (turnOutcome as NonNullable<typeof turnOutcome>).errorCode,
        )}
        onStartNewChat={onStartNewChat}
        onUpgradePlan={onUpgradePlan}
        onRetry={canRetry ? handleRetry : undefined}
      />
    );
  }

  if (showFullLoader) {
    return (
      <div className={styles.conversation}>
        <div className={styles.stateFill}>
          <div className={styles.emptyState}>
            <CircleLoader />
          </div>
        </div>
        <div className={styles.inputWrap}>
          <ChatInput
            disabled
            credits={aiCredits ?? undefined}
            placeholder={texts.inputPlaceholder}
            onSend={handleSend}
          />
        </div>
      </div>
    );
  }

  return (
    <AiChatInsertContext.Provider value={insertIntoChat}>
      <div className={styles.conversation}>
        {isEmpty ? (
          <div className={styles.stateFill}>
            <ConversationEmptyState
              texts={texts}
              disabled={composerHidden}
              onSuggestionSelect={handleSend}
            />
          </div>
        ) : (
          <>
            {loadingOlder && (
              <div className={styles.olderLoader}>
                <CircleLoader />
              </div>
            )}
            <div
              ref={registerScrollNode}
              className={styles.messageScroll}
              onScroll={handleListScroll}
            >
              <div ref={setContentNode} className={styles.messageList}>
                {rows.map((row) => (
                  <Fragment key={row.key}>{renderRow(row)}</Fragment>
                ))}
              </div>
            </div>
            {/* Only while the viewport is detached from the bottom: parked
                there, following is automatic and the control would say nothing.
                Zero-height row, so revealing it never resizes the scroller
                (which the observer would read as growth and follow). */}
            {!pinned && (
              <div className={styles.jumpToBottom}>
                <IconButton
                  aria-label={t('ai-scroll-to-latest')}
                  className={styles.jumpToBottomButton}
                  icon="arrow_downward"
                  rounded
                  size={IconButtonSizes.Small}
                  symbolsOutlined
                  type={IconButtonTypes.SecondaryInvert}
                  onClick={pinToBottom}
                />
              </div>
            )}
          </>
        )}
        {composerHidden ? (
          showUnavailableNotice && (
            <div className={classNames(styles.inputWrap, styles.noticeWrap)}>
              {/* The notice only shows for a live block (a turn that failed
                  with an unavailable code is excluded above and carries its own
                  banner), so the reason is always set — the fallback copy is
                  only here to satisfy the type. */}
              <AiErrorMessage
                text={
                  getAiBudgetBlockedText(t, aiBlockedReason) ??
                  t('ai-unavailable-no-credits')
                }
                action={getAiBudgetBlockedAction(aiBlockedReason)}
                onUpgradePlan={onUpgradePlan}
              />
            </div>
          )
        ) : (
          <div className={styles.inputWrap}>
            <ChatInput
              ref={chatInputRef}
              initialDocs={initialDocs}
              disabled={isGenerating}
              isGenerating={isGenerating}
              credits={aiCredits ?? undefined}
              placeholder={texts.inputPlaceholder}
              onSend={handleSend}
              onStop={stopGeneration}
            />
          </div>
        )}
      </div>
      <AiDislikeFeedbackModal
        isOpen={!!dislikeTargetId}
        formKey={dislikeTargetId ?? 'closed'}
        loading={feedbackLoading}
        onClose={() => setDislikeTargetId(null)}
        onSubmit={handleDislikeSubmit}
      />
    </AiChatInsertContext.Provider>
  );
}

export default ConversationPane;
